import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { kiosk } from "../kioskTheme";

const CAM_ID = "plate-camera-sheet-cam";

/** 安全停止（同 ScanViewport：stop() 在非掃描中會同步 throw，需先查狀態＋try/catch）。 */
const stopSafely = (s: Html5Qrcode): void => {
  try {
    const st = s.getState();
    if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) {
      s.stop().then(() => s.clear()).catch(() => {});
    } else {
      s.clear();
    }
  } catch {
    /* 尚未 start / 已停止：忽略 */
  }
};

interface PlateCameraSheetProps {
  open: boolean;
  onClose: () => void;
  /** 拍到照片（已轉 File）。 */
  onCapture: (file: File) => void;
  /**
   * 鋼瓶掃描開關（supplier.system_setting.cylinder_scan）開啟時才會讀碼：
   * 開相機 5 秒內以「拍照」為主不掃描；5 秒後仍未拍 → 背景開始嘗試讀 barcode，
   * 讀到即回呼；拍照當下也會對照片再試讀一次。開關關閉 → 純拍照，完全不讀碼。
   */
  scanEnabled: boolean;
  /** 讀到 barcode（即時掃到或照片解出）。 */
  onBarcode?: (code: string, sourceFile?: File) => void;
}

/**
 * 鐵牌「開鏡頭拍照」全屏 sheet（拍照優先）：
 * 相機一開就能拍；5 秒未拍且掃描開關開啟 → 背景同步嘗試讀 barcode（不打斷拍照）。
 */
const PlateCameraSheet = ({ open, onClose, onCapture, scanEnabled, onBarcode }: PlateCameraSheetProps) => {
  const [camError, setCamError] = useState(false);
  const [scanningHint, setScanningHint] = useState(false); // 5 秒後開始背景讀碼 → 顯示小提示
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const decodedRef = useRef(false); // 只回報一次 barcode
  const onBarcodeRef = useRef(onBarcode);
  useEffect(() => {
    onBarcodeRef.current = onBarcode;
  });
  const scanEnabledRef = useRef(scanEnabled);
  useEffect(() => {
    scanEnabledRef.current = scanEnabled;
  });

  useEffect(() => {
    if (!open) return;
    setCamError(false);
    setScanningHint(false);
    decodedRef.current = false;
    let cancelled = false;
    let scanArmed = false; // 5 秒後才 true：此前 decode callback 一律忽略（拍照優先）
    // 延後啟動（同 ScanViewport：避免 StrictMode 假 mount 的 play() interrupted）
    const startTimer = setTimeout(async () => {
      try {
        const s = new Html5Qrcode(CAM_ID, { verbose: false });
        scannerRef.current = s;
        if (cancelled) return;
        // 相機常開（拍照用）；decode callback 常駐，但 scanArmed=false 時忽略 → 等同「還沒開始掃」
        await s.start(
          { facingMode: "environment" },
          { fps: 10 },
          (text: string) => {
            if (!scanArmed || !scanEnabledRef.current || decodedRef.current) {
              // eslint-disable-next-line no-console
              console.log(`[PlateCamera] 讀到條碼但忽略（armed=${scanArmed} scanEnabled=${scanEnabledRef.current} already=${decodedRef.current}）:`, text);
              return;
            }
            decodedRef.current = true;
            // eslint-disable-next-line no-console
            console.log("[PlateCamera] 即時掃描讀到條碼 →", text);
            onBarcodeRef.current?.(text);
          },
          () => {},
        );
        // eslint-disable-next-line no-console
        console.log(`[PlateCamera] 相機已啟動（scanEnabled=${scanEnabledRef.current}；5 秒未拍照將開始背景讀碼）`);
        if (cancelled) stopSafely(s);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[PlateCameraSheet] camera start failed:", err);
        if (!cancelled) setCamError(true);
      }
    }, 0);
    // 開鏡頭 5 秒仍未拍照 → 開始背景讀碼（僅掃描開關開啟時有意義）
    const armTimer = setTimeout(() => {
      scanArmed = true;
      // eslint-disable-next-line no-console
      console.log(`[PlateCamera] 開鏡頭滿 5 秒未拍照 → 背景讀碼${scanEnabledRef.current ? "啟動" : "不啟動（掃描開關關閉）"}`);
      if (scanEnabledRef.current) setScanningHint(true);
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      clearTimeout(armTimer);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) stopSafely(s);
    };
  }, [open]);

  /** 快門：抓 video 當前影格 → File。拍完（掃描開關開啟時）對照片再試讀一次 barcode。 */
  const capture = async () => {
    const video = document.querySelector<HTMLVideoElement>(`#${CAM_ID} video`);
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `plate-${Date.now()}.jpg`, { type: "image/jpeg" });
      // eslint-disable-next-line no-console
      console.log(`[PlateCamera] 已拍照（已加入照片清單）；scanEnabled=${scanEnabledRef.current} → ${scanEnabledRef.current ? "接著嘗試從照片解碼" : "不解碼"}`);
      onCapture(file);
      // 照片再試讀一次 barcode（需先停相機，html5-qrcode 掃描中不可 scanFile）
      if (scanEnabledRef.current && !decodedRef.current) {
        const s = scannerRef.current;
        if (s) {
          scannerRef.current = null;
          try {
            const st = s.getState();
            if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) {
              await s.stop().catch(() => {});
            }
            // eslint-disable-next-line no-console
            console.log("[PlateCamera] 已拍照，嘗試從照片解 barcode…");
            const result = await s.scanFileV2(file, false);
            if (result?.decodedText && !decodedRef.current) {
              decodedRef.current = true;
              // eslint-disable-next-line no-console
              console.log("[PlateCamera] 照片解出條碼 →", result.decodedText);
              onBarcodeRef.current?.(result.decodedText, file);
            }
          } catch {
            // eslint-disable-next-line no-console
            console.log("[PlateCamera] 照片解不出條碼（正常，非每張都有碼）");
          } finally {
            try { s.clear(); } catch { /* noop */ }
          }
        }
      }
      onClose();
    }, "image/jpeg", 0.92);
  };

  if (!open) return null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 1000006, background: "#10151c", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", color: "#fff" }}>
        <span style={{ fontSize: 15, fontWeight: 800 }}>拍攝鋼瓶鐵牌</span>
        <span onClick={onClose} style={{ fontSize: 14, opacity: 0.85, cursor: "pointer" }}>取消</span>
      </div>

      {/* 相機預覽（html5-qrcode 會把 video 塞進此容器） */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}>
        <div id={CAM_ID} style={{ position: "absolute", inset: 0 }} />
        {camError && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.75)", fontSize: 13, padding: 24, textAlign: "center" }}>
            無法開啟相機；請改用「從相簿選擇」，或檢查瀏覽器相機權限
          </div>
        )}
        {scanningHint && !camError && (
          <div style={{ position: "absolute", top: 10, left: 0, right: 0, textAlign: "center" }}>
            <span style={{ fontSize: 12, color: "#fff", background: "rgba(33,150,243,.75)", borderRadius: 999, padding: "4px 12px" }}>
              也在幫您辨識鐵牌條碼中…（不影響拍照）
            </span>
          </div>
        )}
      </div>

      {/* 快門 */}
      <div style={{ padding: "18px 0 30px", display: "flex", justifyContent: "center" }}>
        <button
          onClick={capture}
          disabled={camError}
          aria-label="拍照"
          style={{
            width: 68,
            height: 68,
            borderRadius: "50%",
            border: "4px solid #fff",
            background: camError ? "#5b6472" : kiosk.blue,
            cursor: camError ? "default" : "pointer",
          }}
        />
      </div>
    </div>,
    document.body,
  );
};

export default PlateCameraSheet;
